// Explainer articles — factual, general education only (no personal advice,
// no ratings, no "investment grade" or "senior secured" labels).
// Market figures are sourced from the owner's market plan (EY-Parthenon data).

export const ARTICLES = [
  {
    slug: "private-credit-vs-bank-lending",
    tag: "TOPIC 01",
    title: "Private Credit vs Bank Lending in Australia",
    description:
      "How private credit differs from bank lending in Australia: speed, covenants, structure and price — and why borrowers increasingly use both channels.",
    excerpt:
      "Private credit has grown into a A$234.5b market by doing what banks often cannot: moving faster, structuring around the asset, and writing loans outside standard credit policy.",
    readMinutes: 6,
    body: [
      {
        h: "Two channels, one borrower need",
        paragraphs: [
          "Australian businesses and property developers have historically relied on the major banks for debt. Private credit — loans originated by non-bank fund managers using investor capital — has emerged as a parallel channel. Australian private credit reached A$234.5b in 2025, compounding at 21.0% a year since 2015, against 5.5% annual growth in bank lending over the same period.",
          "The two channels are not simply substitutes. Banks fund loans from deposits and operate under APRA's prudential framework, which shapes what they can write and how quickly. Private credit funds answer to their investors and investment mandates, which gives them different constraints — and different freedoms.",
        ],
      },
      {
        h: "Speed and certainty of execution",
        paragraphs: [
          "The most frequently cited reason borrowers approach private credit is speed. A fund with capital in hand and a small credit committee can often issue terms in weeks, where a bank credit process may take months, particularly for transactions that sit outside standard policy — land banks, construction with complex presale profiles, or corporate borrowers with lumpy cash flow.",
          "Certainty matters as much as speed. A borrower settling a site acquisition on a fixed date may accept a higher interest rate in exchange for a lender that can commit and settle on time.",
        ],
      },
      {
        h: "Structure: covenants, amortisation and security",
        paragraphs: [
          "Bank loans tend to be standardised: preset leverage limits, regular amortisation, and covenant packages drawn from bank policy. Private credit facilities are typically bespoke. A manager may structure interest-only periods through construction, allow capitalised interest, take security over a broader pool of assets, or size a facility against the completed value of a project rather than its current value.",
          "This flexibility is not free. Private credit pricing generally sits above bank pricing, reflecting the illiquidity investors accept and the complexity of the loans written. Borrowers should compare total cost — establishment fees, line fees, default interest and exit fees — not just the headline margin.",
        ],
      },
      {
        h: "Where each channel typically wins",
        paragraphs: [
          "Banks remain the natural home for vanilla lending: stabilised assets, strong borrowers, simple structures, and borrowers who value the lowest available cost of debt. Private credit tends to compete where the transaction is complex, time-critical, or in sectors banks have stepped back from — notably parts of commercial real estate and mid-market corporate lending.",
          "Many borrowers now run both channels in parallel: a bank facility for the core balance sheet and private credit for a specific project, acquisition or bridging requirement.",
        ],
      },
      {
        h: "Questions borrowers should ask a private lender",
        paragraphs: [
          "How is the fund capitalised, and is the capital committed? Who approves the loan and how long does credit approval genuinely take? What are all the fees, including exit and default charges? What reporting and covenants will apply during the loan? And what happens if the project timeline slips — how has the manager behaved through previous workouts?",
        ],
      },
    ],
  },
  {
    slug: "fees-and-conflicts",
    tag: "TOPIC 02",
    title: "Fees and Conflicts in Australian Private Credit Funds",
    description:
      "A plain-English guide to private credit fund fees — management, performance and establishment fees — and the conflict disclosures ASIC now expects to see.",
    excerpt:
      "ASIC's REP 814 put fund fees and manager conflicts at the centre of its private credit review. Here is what the common fee lines mean and where interests can diverge.",
    readMinutes: 7,
    body: [
      {
        h: "Why fees are under the spotlight",
        paragraphs: [
          "ASIC's Report 814 (September 2025) examined private credit funds and flagged fee practices and conflicts of interest as areas requiring better disclosure. The regulator's concern is straightforward: private credit is sold largely to wholesale investors on the basis of income, but the net income an investor receives depends on how many layers of fees sit between the borrower and the investor.",
        ],
      },
      {
        h: "The common fee lines",
        paragraphs: [
          "Management fees are charged by the manager for running the fund, usually as a percentage of funds under management or invested capital. Performance fees (or carried interest) reward the manager above a return hurdle. Establishment and arrangement fees are paid by the borrower when a loan is originated.",
          "The question REP 814 sharpened is who keeps the borrower-paid fees. If the manager retains establishment, exit and late fees, the manager earns more on a loan than the investors whose capital is at risk — a direct divergence of interest. Some managers rebate these fees to the fund in full; others split or retain them. The fund's disclosure documents should state the treatment plainly.",
        ],
      },
      {
        h: "Conflicts that matter in practice",
        paragraphs: [
          "Related-party lending — where a fund lends to entities connected to the manager — concentrates both return and risk inside one group and deserves close scrutiny. So does cross-fund allocation: when a manager runs several funds, which fund gets which loan, and at what price? Valuation conflicts are covered in our REP 814 explainer, but they interact with fees: a manager paid on net asset value has an incentive to hold asset values high.",
        ],
      },
      {
        h: "Reading the disclosure",
        paragraphs: [
          "For wholesale investors, the key documents are the information memorandum and any fee and conflicts policy the manager publishes. Useful checks: the total expense load expressed as a single annual figure; the treatment of borrower-paid fees; whether the manager or its staff co-invest; how related-party loans are approved; and whether an independent valuation agent is used and how often.",
          "None of this is personal advice — it is a reading list. Investors should obtain professional advice on any specific fund before committing capital.",
        ],
      },
    ],
  },
  {
    slug: "valuations-asic-rep-814",
    tag: "TOPIC 03",
    title: "Valuations Under the Microscope: ASIC REP 814 Explained",
    description:
      "What ASIC Report 814 found on private credit valuations, why independent valuation matters for illiquid loans, and what investors should look for.",
    excerpt:
      "Private credit assets do not trade on a screen, so reported returns depend on how managers value them. ASIC's REP 814 made valuation governance a front-line issue.",
    readMinutes: 7,
    body: [
      {
        h: "The problem REP 814 addresses",
        paragraphs: [
          "A listed bond reprices every second. A private loan to a property developer does not trade at all — its value exists only as an estimate. When investors subscribe and redeem at prices based on those estimates, valuation policy stops being an accounting detail and becomes the mechanism that determines whether incoming investors subsidise outgoing ones, or vice versa.",
          "ASIC's Report 814, released in September 2025, reviewed how Australian private credit funds value assets, manage conflicts and disclose fees — and signalled that weak valuation governance will attract regulatory attention.",
        ],
      },
      {
        h: "What good valuation governance looks like",
        paragraphs: [
          "The features regulators and researchers consistently point to: valuations performed by genuinely independent valuers on a defined cycle; trigger events that force a revaluation when a loan deteriorates rather than waiting for the calendar; a valuation committee with documented challenge; and disclosure to investors of the methodology, the frequency and any reliance on director valuations.",
          "For loans secured over property, independence is especially important, because the value of the security drives the loan's impairment status. A valuation commissioned and paid for by the manager, from a valuer with an ongoing commercial relationship, is weaker evidence than one from an independent panel.",
        ],
      },
      {
        h: "Labels and loose language",
        paragraphs: [
          "REP 814 also took aim at marketing language. Terms like 'investment grade' have a defined meaning in rated bond markets and no defined meaning in private credit, where no rating agency has assessed the loan. Using bond-market vocabulary for unrated private loans can mislead investors about the risk they are taking, which is why this site avoids such labels entirely.",
        ],
      },
      {
        h: "What to ask a manager",
        paragraphs: [
          "Who values the portfolio and how often? What triggers an out-of-cycle revaluation? Have any loans been written down, and how were investors told? Are borrower-paid fees used to smooth distributions? How does the fund price units when loans are impaired? Straight answers to these questions are a baseline expectation in the post-REP 814 environment, not a courtesy.",
        ],
      },
    ],
  },
  {
    slug: "real-estate-credit",
    tag: "TOPIC 04",
    title: "Real Estate Credit: Australia's Largest Private Debt Segment",
    description:
      "How real estate private credit works in Australia — construction finance, land and investment loans, mezzanine structures, and the risks investors assess.",
    excerpt:
      "Commercial real estate debt is the engine room of Australian private credit. Here is how the common structures work and what separates prudent lending from stretch.",
    readMinutes: 7,
    body: [
      {
        h: "Why real estate dominates",
        paragraphs: [
          "Much of Australia's A$234.5b private credit market is secured against commercial real estate: residential development sites, industrial assets, offices, retail and land banks. The retreat of the major banks from segments of development finance — driven by prudential settings and risk appetite — created a funding gap that non-bank lenders and credit funds moved into.",
        ],
      },
      {
        h: "The common facility types",
        paragraphs: [
          "Construction finance funds a project from slab to sell-down, typically with progressive drawdowns against certified work, quantity surveyor oversight and presale hurdles. Investment (or residual stock) loans refinance completed but unsold stock. Land bank facilities lend against undeveloped sites ahead of approvals. Mezzanine facilities sit behind a first-ranking lender to stretch total leverage, at materially higher pricing to compensate for the thinner equity cushion.",
          "Each structure allocates risk differently. The further a lender sits from first-ranking security, the more the return depends on the sponsor's execution and the final sale market.",
        ],
      },
      {
        h: "What underwriters actually assess",
        paragraphs: [
          "Beyond the loan-to-value ratio, experienced credit teams focus on the sponsor's delivery record, the realism of the feasibility (construction costs, programme, sales rates and prices), the contingency in the budget, the quality of presales, and the exit — whether the loan is repaid from sales, refinance, or asset sale. The exit matters most: private credit loans are short-dated, and every loan is, in effect, a bet on a specific repayment event.",
        ],
      },
      {
        h: "Signals investors watch in this segment",
        paragraphs: [
          "For investors in real estate credit funds, the instructive questions are about concentration (single project, sponsor or geography), arrears recognition — how quickly does the fund acknowledge a troubled loan — and valuation practice, which ASIC's REP 814 brought into focus. A fund showing smooth returns with zero arrears through a construction downturn is a fund whose valuation policy deserves questions, not congratulations.",
        ],
      },
    ],
  },
  {
    slug: "corporate-facilities",
    tag: "TOPIC 05",
    title: "Corporate Private Credit: Facilities Beyond the Banks",
    description:
      "Corporate private credit in Australia: direct lending to mid-market companies, acquisition finance, and cash flow loans — structures, risks and questions.",
    excerpt:
      "Beyond real estate, private credit funds lend directly to Australian companies — term loans, acquisition finance and bespoke facilities the banks will not write.",
    readMinutes: 6,
    body: [
      {
        h: "The corporate direct lending market",
        paragraphs: [
          "Corporate private credit covers loans made by non-bank funds to operating businesses: mid-market companies funding growth, acquisitions, recapitalisations or working capital. Australian managers active here include specialist corporate lenders and diversified managers running direct lending mandates alongside their real estate books.",
          "The borrower base is typically the mid-market — companies large enough to support institutional-sized loans but too small, too leveraged or too complex for the syndicated bank market.",
        ],
      },
      {
        h: "Cash flow lending versus asset-backed lending",
        paragraphs: [
          "Corporate facilities are usually underwritten against enterprise cash flow rather than hard assets. The lender's security is a charge over the business, and the real protection is the borrower's ability to generate earnings through the loan term. That makes diligence different from property lending: industry dynamics, customer concentration, management quality and covenant headroom matter more than a valuation of bricks and land.",
          "Specialist variations include acquisition finance supporting private equity transactions, unitranche facilities that blend first and second ranking debt into one instrument, and recurring-revenue lending against contracted subscription income.",
        ],
      },
      {
        h: "Covenants: the lender's early warning system",
        paragraphs: [
          "Because corporate loans cannot rely on a sale of security, covenants do the heavy lifting. Leverage and interest-cover tests force a conversation early, while earnings are still repairable. Investors in corporate credit funds should ask how covenant packages are set, how often borrowers report, and what the manager's workout process looks like when a covenant trips — the answer distinguishes lenders from passengers.",
        ],
      },
      {
        h: "What this means for borrowers",
        paragraphs: [
          "For a mid-market company, private credit offers a single-lender relationship, bespoke amortisation and faster execution than a bank syndicate, at a higher cost of funds. The borrower's checklist mirrors the lender's: understand every fee, the covenant package, the reporting burden, and how the manager has treated borrowers through difficult periods before signing.",
        ],
      },
    ],
  },
  {
    slug: "regulatory-watch",
    tag: "TOPIC 06",
    title: "The Regulatory Perimeter: ASIC's Focus on Private Credit",
    description:
      "How ASIC is reshaping Australian private credit: REP 814, data collection pilots, industry standards work, and what tighter oversight means for the market.",
    excerpt:
      "A A$234.5b market growing at 21% a year was never going to stay outside the regulatory spotlight. Here is what ASIC has done, and what is likely next.",
    readMinutes: 6,
    body: [
      {
        h: "How the spotlight arrived",
        paragraphs: [
          "Three forces put private credit on ASIC's agenda. Scale: the market reached A$234.5b in 2025 and is forecast to approach A$300b by 2029 (EY-Parthenon). Velocity: there are roughly three times as many funds as in 2017. And access: products once confined to institutions are now marketed to a much wider wholesale audience.",
        ],
      },
      {
        h: "Report 814 and what it found",
        paragraphs: [
          "ASIC's Report 814 (September 2025) is the centrepiece. It examined fees, conflicts of interest, valuation practices and marketing language across the sector, and set expectations rather than new law: independent valuations, transparent fee treatment, genuine management of related-party conflicts, and an end to borrowed bond-market labels on unrated private loans.",
          "Enforcement posture has followed. ASIC has intervened in individual funds where disclosure or governance fell short, signalling that REP 814 is an operating standard, not a discussion paper.",
        ],
      },
      {
        h: "Data collection and industry standards",
        paragraphs: [
          "Beyond individual reports, ASIC has been building its data picture of the sector through collection pilots, and working with industry bodies on shared standards for the asset class. The direction of travel is consistent: private credit will be measured, compared and supervised more like the public markets it increasingly rivals in size.",
        ],
      },
      {
        h: "What tighter oversight means for the market",
        paragraphs: [
          "For managers, the compliance bar is rising — documentation, valuation independence and marketing review all carry real regulatory risk. For investors and advisers, better disclosure makes funds easier to compare. And for the market's infrastructure — directories, data and neutral education like this site — regulatory scrutiny increases the value of accurate, general, well-sourced information. This site does not provide personal advice; readers should obtain professional advice before acting on anything here.",
        ],
      },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
